var _a, _b, _c;
import { CartLinesUpdateEvent } from "@shopify/events";
import { fetchCartJson, fetchWithDeadline, setCartCheckout } from "@theme/utilities";
const text = JSON.parse(((_a = document.getElementById("pb-card-add-text")) == null ? void 0 : _a.textContent) || "{}");
const root = ((_c = (_b = window.Shopify) == null ? void 0 : _b.routes) == null ? void 0 : _c.root) || "/";
let dialog;
let owner;
let pickerGeneration = 0;
let pickerController;
let pendingAdd = false;
const cardCheckoutOwner = {};
let reviewMessage = "";
function showMessage(region, message, review) {
  region.textContent = message;
  if (review) {
    const link = document.createElement("a");
    link.href = root + "cart";
    link.className = "pb-card-review-link";
    link.textContent = text.view_cart;
    region.append(link);
  }
}
function announce(message, review = false) {
  let region = document.getElementById("pb-card-add-status");
  if (!region) {
    region = document.createElement("div");
    region.id = "pb-card-add-status";
    region.className = "pb-card-add-status";
    region.setAttribute("role", "status");
    document.body.append(region);
  }
  showMessage(region, message, review);
  region.classList.toggle("pb-card-add-status--review", review);
  region.hidden = false;
  clearTimeout(region.hideTimer);
  if (review) return;
  region.hideTimer = setTimeout(() => {
    region.hidden = true;
  }, 6500);
}
function getDialog() {
  if (dialog) return dialog;
  dialog = document.createElement("dialog");
  dialog.className = "pb-card-picker-dialog";
  dialog.setAttribute("aria-labelledby", "pb-card-picker-title");
  document.body.append(dialog);
  dialog.addEventListener("click", (event) => {
    if (event.target.closest("[data-pb-close]") || event.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => {
    if (reviewMessage) announce(reviewMessage, true);
    var _a2;
    if (!owner) return;
    const focusLink = owner.querySelector("a");
    if (focusLink) focusLink.focus({ preventScroll: true });
  });
  dialog.addEventListener("change", (event) => {
    const select = event.target;
    if (!select.matches('[data-pb-picker-form] select[name="id"]')) return;
    if (!select.value || !select.selectedOptions[0] || select.selectedOptions[0].disabled) return;
    const photo = dialog.querySelector(".pb-card-picker__header img");
    const option = select.selectedOptions[0];
    if (photo) {
      photo.hidden = !option.dataset.image;
      if (option.dataset.image) {
        photo.removeAttribute("srcset");
        photo.removeAttribute("sizes");
        photo.src = option.dataset.image;
        photo.alt = option.dataset.imageAlt || "";
      }
    }
    const details = dialog.querySelector(".pb-card-picker__details");
    if (!details) return;
    const url = new URL(details.href);
    url.searchParams.set("variant", select.value);
    details.href = url.href;
  });
  dialog.addEventListener("submit", async (event) => {
    if (!event.target.matches("[data-pb-picker-form]")) return;
    event.preventDefault();
    const select = event.target.elements.id;
    if (!select.value || !owner || !select.selectedOptions[0] || select.selectedOptions[0].disabled || pendingAdd) return;
    const activeOwner = owner;
    const submit = event.target.querySelector('[type="submit"]');
    const error = dialog.querySelector("[data-pb-picker-error]");
    submit.disabled = true;
    error.textContent = "";
    try {
      await activeOwner.add(select.value, Number(select.selectedOptions[0].dataset.min) || 1);
    } catch (failure) {
      if (dialog.open && owner === activeOwner) showMessage(error, failure.message, Boolean(reviewMessage));
      else announce(failure.message, Boolean(reviewMessage));
    } finally {
      submit.disabled = Boolean(reviewMessage);
    }
  });
  return dialog;
}
class PBCardAdd extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.addEventListener("click", (event) => this.activate(event));
  }
  disconnectedCallback() {
    if (this.pickerController) this.pickerController.abort();
    if (this.addController) this.addController.abort();
    clearTimeout(this.addedTimer);
    delete this.dataset.added;
  }
  async activate(event) {
    var _a2;
    const link = event.target.closest("a");
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.getAttribute("aria-disabled") === "true" || this.hasAttribute("aria-busy")) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (this.dataset.mode === "configure") return;
    event.preventDefault();
    event.stopPropagation();
    if (pendingAdd) {
      announce(text.adding);
      return;
    }
    if (reviewMessage) {
      announce(reviewMessage, true);
      return;
    }
    this.setAttribute("aria-busy", "true");
    const generation = ++pickerGeneration;
    if (pickerController) pickerController.abort();
    try {
      if (this.dataset.mode === "add") {
        await this.add(this.dataset.variant, Number(this.dataset.quantity) || 1);
      } else {
        const url = new URL(link.href);
        url.searchParams.set("section_id", "pb-card-picker");
        const controller = new AbortController();
        this.pickerController = pickerController = controller;
        const html = await fetchWithDeadline(url, { headers: { Accept: "text/html" } }, controller, 15e3, async (response) => {
          if (!response.ok) throw new Error(text.error);
          return response.text();
        });
        if (generation !== pickerGeneration || !this.isConnected) return;
        const documentFragment = new DOMParser().parseFromString(html, "text/html");
        const content = documentFragment.querySelector("[data-pb-picker]");
        if (!content) throw new Error(text.error);
        if (this.dataset.piece) {
          content.querySelectorAll("option[data-options]").forEach((option) => {
            if (!JSON.parse(option.dataset.options).includes(this.dataset.piece)) option.remove();
          });
        }
        if (!content.querySelector('option[value]:not([value=""]):not([disabled])')) {
          throw new Error(text.sold_out);
        }
        owner = this;
        const modal = getDialog();
        modal.replaceChildren(content);
        modal.querySelector(".pb-card-picker__details").href = link.href;
        modal.showModal();
        (_a2 = modal.querySelector("select")) == null ? void 0 : _a2.focus();
      }
    } catch (error) {
      if (reviewMessage) announce(reviewMessage, true);
      else if (error.name !== "AbortError" && generation === pickerGeneration) announce(text.error);
    } finally {
      this.pickerController = null;
      this.removeAttribute("aria-busy");
    }
  }
  async add(id, quantity) {
    if (this.adding || pendingAdd) return;
    if (reviewMessage) throw new Error(reviewMessage);
    if (!Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !Number.isInteger(quantity) || quantity <= 0) throw new Error(text.error);
    this.adding = pendingAdd = true;
    setCartCheckout(cardCheckoutOwner, "pending");
    this.setAttribute("aria-busy", "true");
    let confirmed = false;
    try {
      const sections = [...new Set([...document.querySelectorAll("cart-items-component[data-section-id]")].map((e) => e.dataset.sectionId).filter(Boolean))].slice(0, 5);
      this.addController = new AbortController();
      const result = await fetchCartJson(root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(id), quantity }], sections, sections_url: location.pathname })
      }, this.addController, 3e4);
      const lines = result && Array.isArray(result.items) ? result.items : [result];
      if (!result || result.status || !lines.some((line) => line && String(line.variant_id || line.id) === String(id) && Number(line.quantity) > 0)) throw new Error(text.review);
      confirmed = true;
      this.addController = new AbortController();
      const cart = await fetchCartJson(root + "cart.js", {
        headers: { Accept: "application/json" },
        cache: "no-store"
      }, this.addController, 15e3);
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error(text.added_review);
      this.addController = null;
      if (dialog && dialog.open && owner === this) dialog.close();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: "add",
        context: "standard-action",
        lines: [{ merchandiseId: String(id), quantity }],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections: result.sections, source: "pb-card-add", sourceId: "pb-card-add-" + id, itemCount: quantity, didError: false }
        })
      }));
      this.dataset.added = "true";
      announce(text.added);
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => {
        delete this.dataset.added;
      }, 1800);
    } catch (error) {
      reviewMessage = confirmed ? text.added_review : text.review;
      if (!confirmed && (error.status === 400 || error.status === 422) && error.message) reviewMessage = error.message + " " + text.review;
      throw new Error(reviewMessage);
    } finally {
      this.addController = null;
      this.adding = pendingAdd = false;
      setCartCheckout(cardCheckoutOwner, reviewMessage ? "failed" : null);
      this.removeAttribute("aria-busy");
    }
  }
}
if (!customElements.get("pb-card-add")) customElements.define("pb-card-add", PBCardAdd);
